import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { MediaUploadPrepareStatus } from "../types/media";
import { DEFAULT_ACCEPTED_MEDIA_TYPES, isAcceptedMediaFile } from "../utils/acceptedMediaFiles";

const IDLE_PREPARE_STATUS: MediaUploadPrepareStatus = { phase: "idle" };
const PROCESS_CHUNK_SIZE = 16;
const WAITING_UI_DELAY_MS = 3000;
const DEFAULT_MAX_FILES = 500;

function yieldToMain(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 0);
  });
}

type UsePickMediaForUploadOptions = {
  onFilesReady: (files: File[]) => void;
  maxFiles?: number;
  acceptedTypes?: string[];
  disabled?: boolean;
};

export function usePickMediaForUpload({
  onFilesReady,
  maxFiles = DEFAULT_MAX_FILES,
  acceptedTypes = DEFAULT_ACCEPTED_MEDIA_TYPES,
  disabled = false,
}: UsePickMediaForUploadOptions) {
  const [status, setStatus] = useState<MediaUploadPrepareStatus>(IDLE_PREPARE_STATUS);
  const [showWaitingUi, setShowWaitingUi] = useState(false);
  const [emittedAnyFiles, setEmittedAnyFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processGenerationRef = useRef(0);
  const emittedCountRef = useRef(0);
  const waitingStartedAtRef = useRef(0);

  const isPreparing = status.phase !== "idle";
  const showPrepareUi =
    (status.phase === "waiting-for-files" && showWaitingUi) ||
    (status.phase === "processing" && !emittedAnyFiles);

  useEffect(() => {
    if (status.phase !== "waiting-for-files") {
      setShowWaitingUi(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowWaitingUi(true);
    }, WAITING_UI_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [status.phase]);

  useEffect(() => {
    if (status.phase !== "waiting-for-files") {
      return;
    }

    const markStillWaiting = () => {
      if (Date.now() - waitingStartedAtRef.current < WAITING_UI_DELAY_MS) {
        return;
      }
      setShowWaitingUi(true);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        markStillWaiting();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", markStillWaiting);
    window.addEventListener("pageshow", markStillWaiting);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", markStillWaiting);
      window.removeEventListener("pageshow", markStillWaiting);
    };
  }, [status.phase]);

  const cancel = useCallback(() => {
    processGenerationRef.current += 1;
    emittedCountRef.current = 0;
    setEmittedAnyFiles(false);
    setStatus(IDLE_PREPARE_STATUS);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const processFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) {
        setStatus(IDLE_PREPARE_STATUS);
        return;
      }

      const generation = processGenerationRef.current + 1;
      processGenerationRef.current = generation;
      emittedCountRef.current = 0;
      setEmittedAnyFiles(false);

      const total = files.length;
      setStatus({ phase: "processing", processed: 0, total });

      const accepted: File[] = [];
      let hasFlushedFirstFile = false;

      const flush = (processed: number) => {
        const pending = accepted.slice(emittedCountRef.current);
        if (pending.length > 0) {
          emittedCountRef.current = accepted.length;
          setEmittedAnyFiles(true);
          onFilesReady(pending);
        }
        setStatus({ phase: "processing", processed, total });
      };

      for (let i = 0; i < total; i++) {
        if (generation !== processGenerationRef.current) {
          return;
        }

        const file = files[i];
        if (accepted.length < maxFiles && isAcceptedMediaFile(file, acceptedTypes)) {
          accepted.push(file);
        }

        const processed = i + 1;
        const shouldFlushFirstFile = !hasFlushedFirstFile && accepted.length > 0;
        if (shouldFlushFirstFile || processed % PROCESS_CHUNK_SIZE === 0 || processed === total) {
          hasFlushedFirstFile = true;
          flush(processed);
          await yieldToMain();
        }
      }

      if (generation !== processGenerationRef.current) {
        return;
      }

      setStatus(IDLE_PREPARE_STATUS);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [acceptedTypes, maxFiles, onFilesReady],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      void processFiles(event.target.files);
    },
    [disabled, processFiles],
  );

  useEffect(() => {
    const input = fileInputRef.current;
    if (!input) {
      return;
    }

    const onCancel = () => {
      cancel();
    };

    input.addEventListener("cancel", onCancel);
    return () => {
      input.removeEventListener("cancel", onCancel);
    };
  }, [cancel]);

  const openPicker = useCallback(() => {
    if (disabled || isPreparing) return;
    emittedCountRef.current = 0;
    setEmittedAnyFiles(false);
    waitingStartedAtRef.current = Date.now();
    setStatus({ phase: "waiting-for-files" });
    fileInputRef.current?.click();
  }, [disabled, isPreparing]);

  return {
    status,
    isPreparing,
    showPrepareUi,
    openPicker,
    cancel,
    fileInputRef,
    acceptedTypes,
    handleFileInputChange: handleChange,
  };
}

